import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { com } from "../../proto/game.pb";
import { useWebSocket } from "../../shared/contexts/WebSocketContext";
import { IGameMessage, MT } from "../../shared/types/proto/mt";

export interface UseProvablyFairRoundOptions {
  auto?: boolean;
}

export function useProvablyFairRound(
  roundId?: number | null,
  opts: UseProvablyFairRoundOptions = { auto: true }
) {
  const { auto = true } = opts;
  const { status, onMessage, sendProto } = useWebSocket();

  const [loading, setLoading] = useState(false);
  const [data, setData] =
    useState<com.achlid.crash.game.stateless.IProvablyFairRoundResponse | null>(
      null
    );
  const lastRequestedRoundIdRef = useRef<number | null>(null);

  const request = useMemo(() => {
    return (rId = roundId) => {
      if (status !== "open") return false;
      if (rId == null) return false;
      setLoading(true);
      lastRequestedRoundIdRef.current = Number(rId);
      const msg: IGameMessage = {
        type: MT.GET_PROVABLY_FAIR_ROUND,
        getProvablyFairRound: { roundId: Number(rId) },
      } as any;
      return sendProto(msg);
    };
  }, [status, roundId, sendProto]);

  useEffect(() => {
    const off = onMessage(MT.PROVABLY_FAIR_ROUND_RESPONSE, (msg) => {
      const res = msg.provablyFairRoundResponse;
      if (!res) return;
      const respRoundId = Number(res.roundId ?? 0);
      if (
        lastRequestedRoundIdRef.current != null &&
        respRoundId !== lastRequestedRoundIdRef.current
      )
        return;
      setData(res);
      setLoading(false);
    });
    return () => off();
  }, [onMessage]);

  useEffect(() => {
    if (auto && status === "open" && roundId != null) {
      request(roundId);
    }
  }, [auto, status, roundId, request]);

  return { loading, data, request };
}
